import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, CheckboxControl, SelectControl} from 'appShared/classes/controls';

import {
    AddNewFeedAfxSuffixesSelector,
    AddNewFeedAlaricInternalPriceSelector,
    AddNewFeedBBOSuffixesSelector,
    AddNewFeedCboeOneSuffixesSelector,
    AddNewFeedCfdSuffixesSelector,
    AddNewFeedCostToAlaricNonProSelector,
    AddNewFeedCostToAlaricProSelector,
    AddNewFeedCurrencySelector,
    AddNewFeedDescriptionSelector,
    AddNewFeedExclusionPortalIdsSelector,
    AddNewFeedExcludeSuffixesSelector,
    AddNewFeedFxSuffixesSelector,
    AddNewFeedHasImbalanceSelector,
    AddNewFeedHasL1Selector,
    AddNewFeedHasL2Selector,
    AddNewFeedImbalanceSuffixesSelector,
    AddNewFeedIndividualPriceNonProSelector,
    AddNewFeedIndividualPriceProSelector,
    AddNewFeedL2SuffixesSelector,
    AddNewFeedMarketSelector,
    AddNewFeedNameSelector,
    AddNewFeedNBBOSuffixesSelector,
    AddNewFeedOc5SuffixesSelector,
    AddNewFeedPlatformFrontendIdsSelector,
    AddNewFeedProductCodeForNonProSelector,
    AddNewFeedProductCodeForProSelector,
    AddNewFeedReportTitleIdForNonProSelector,
    AddNewFeedReportTitleIdForProSelector,
    AddNewFeedSuffixPrioritySelector,
    AddNewFeedTradeSuffixesSelector,
    MarketDataFeedFormControls,
    AddNewFeedTseSuffixesSelector,
    AddNewFeedXetraSuffixesSelector,
    AddNewFeedEnxSuffixesSelector,
    AddNewFeedXetraCoreEtfsSuffixesSelector,
} from 'appAssets/descriptors';
import {
    AuthApiService,
    ExchangeModel,
    FeedPriceTypesEnum,
    HttpStatusCodesEnum,
    MarketDataFeedInputModel,
    MarketDataFeedInterface,
    MarketDataFeedModel,
    MarketDataFeedResponseInterface,
    NasdaqReportMapsEnum,
    PlatformFrontendInterface,
    PortalInterface,
    ServerResponse,
    ServerResponseData,
    SesHelper,
    SesService
} from '@spa/api';
import {Helper} from 'appShared/classes';
import {map, switchMap} from 'rxjs/operators';
import {MarketDataFeedFormTypesEnum, MarketDataFeedsFormErrorStatesEnum} from 'appEntries/panel/enums';
import {InArrayValidator} from '@spa/validators';
import {SelectOptionInterface} from 'appShared/components';
import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';
import {BaseMarketDataForm} from 'appShared/forms';
import {CurrencyService} from 'appShared/services';
import {CurrencyTrait} from 'appEntries/panel/traits';

export class AddFeedForm extends BaseMarketDataForm {
    private feed: MarketDataFeedModel;
    private exchanges: ExchangeModel[];
    private readonly platformFrontends: PlatformFrontendInterface[];
    private readonly portals: PortalInterface[];
    private readonly SUFFIX_PRIORITY_LIMIT: number = 100;
    private readonly authenticationService: AuthApiService;
    private readonly currencyService: CurrencyService;
    public formType: MarketDataFeedFormTypesEnum;
    public suffixPrioritiesAutoComplete: SelectOptionInterface[];
    public exchangesAutoComplete: SelectOptionInterface[];
    public platformFrontendsAutoComplete: SelectOptionInterface[];
    public portalsAutoComplete: SelectOptionInterface[];
    public hasL1 = true;
    public hasL2 = false;

    constructor(
        authenticationService: AuthApiService,
        sesService: SesService,
        currencyService: CurrencyService,
        formType: MarketDataFeedFormTypesEnum,
        exchanges: ExchangeModel[],
        platformFrontends: PlatformFrontendInterface[],
        portals: PortalInterface[],
        feed: MarketDataFeedModel = null
    ) {
        super(sesService);
        this.authenticationService = authenticationService;
        this.feed = feed;
        this.exchanges = exchanges;
        this.platformFrontends = platformFrontends;
        this.portals = portals;
        this.formType = formType;
        this.currencyService = currencyService;
    }

    private updateMarketDataFeed(): ServerResponse<boolean | MarketDataFeedModel> {
        const clonedModel = this.feed.serialize();
        const alaricProCost = this.getFormPriceValueByKey(AddNewFeedCostToAlaricProSelector);
        const alaricNonProCost = this.getFormPriceValueByKey(AddNewFeedCostToAlaricNonProSelector);
        const individualPricePro = this.getFormPriceValueByKey(AddNewFeedIndividualPriceProSelector);
        const individualPriceNonPro = this.getFormPriceValueByKey(AddNewFeedIndividualPriceNonProSelector);
        const alaricInternalPrice = this.getFormPriceValueByKey(AddNewFeedAlaricInternalPriceSelector);
        const currency = this.formGroup.value[AddNewFeedCurrencySelector];

        if (this.formType === MarketDataFeedFormTypesEnum.Info) {
            this.updateInfoPart();
        } else if (this.formType === MarketDataFeedFormTypesEnum.ReportMap) {
            this.updateReportMapPart();
        } else {
            this.updateSuffixesPart();
        }

        const inputModel = new MarketDataFeedInputModel(
            +individualPricePro,
            +individualPriceNonPro,
            +alaricInternalPrice,
            +alaricNonProCost,
            +alaricProCost,
            this.feed.bboSuffixSet,
            this.feed.excludedSuffixSet,
            this.feed.imbalanceSuffixSet,
            this.feed.nbboSuffixSet,
            this.feed.l2SuffixSet,
            this.feed.tradeSuffixSet,
            this.feed.name,
            currency,
            this.feed.description,
            this.feed.exchange.id,
            this.feed.hasL1,
            this.feed.hasL2,
            this.feed.platformFrontend.id,
            this.feed.suffixPriority,
            this.feed.reportMap,
            this.feed.fxSuffixSet,
            this.feed.afxSuffixSet,
            this.feed.cfdSuffixSet,
            this.feed.oc5SuffixSet,
            this.feed.cboSuffixSet,
            this.feed.exclusionPortals,
            this.feed.tseSuffixSet,
            this.feed.xetraSuffixSet,
            this.feed.enxSuffixSet,
            this.feed.xetraCoreEtfAndEtpLevel1SuffixSet
        );

        return this.sesService.updateMarketDataFeed(this.feed.id, inputModel).pipe(
            map((registerResponse: ServerResponseData<MarketDataFeedInterface>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    this.feed = new MarketDataFeedModel(clonedModel);

                    return Helper.errorsHelper().createErrorResponse<MarketDataFeedInterface>(
                        registerResponse,
                        MarketDataFeedsFormErrorStatesEnum.UpdateMarketDataFeedFailed
                    );
                }

                const data = registerResponse.data as MarketDataFeedInterface;

                if (data !== null) {
                    this.feed = new MarketDataFeedModel(data);
                    this.hasL1 = data.hasL1;
                    this.hasL2 = data.hasL2;
                }

                return Helper.createSuccessResponse<MarketDataFeedModel>(this.feed);
            })
        );
    }

    private updateReportMapPart(): void {
        const productCodeForProValue = this.formGroup.value[AddNewFeedProductCodeForProSelector];
        if (productCodeForProValue){
            this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForPro] = productCodeForProValue;
        }else {
            delete  this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForPro];
        }

        const productCodeForNonPro = this.formGroup.value[AddNewFeedProductCodeForNonProSelector];
        if (productCodeForNonPro){
            this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForNonPro] = productCodeForNonPro;
        }else {
           delete this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForNonPro];
        }

        const reportTitleForPro = this.formGroup.value[AddNewFeedReportTitleIdForProSelector];
        if (reportTitleForPro){
            this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForPro] = reportTitleForPro;
        }else {
            delete this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForPro];
        }

        const reportTitleForNonPro = this.formGroup.value[AddNewFeedReportTitleIdForNonProSelector];
        if (reportTitleForNonPro){
            this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForNonPro] = reportTitleForNonPro;
        }else {
            delete this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForNonPro];
        }

        if (Object.entries(this.feed.reportMap).length === 0){
           this.feed.reportMap = undefined;
        }
    }

    private updateSuffixesPart() {
        this.feed.hasL1 = this.formGroup.value[AddNewFeedHasL1Selector];
        this.feed.hasL2 = this.formGroup.value[AddNewFeedHasL2Selector];

        this.feed.tradeSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedTradeSuffixesSelector]);
        this.feed.bboSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedBBOSuffixesSelector]);
        this.feed.excludedSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedExcludeSuffixesSelector]);
        this.feed.imbalanceSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedImbalanceSuffixesSelector]);
        this.feed.l2SuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedL2SuffixesSelector]);
        this.feed.nbboSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedNBBOSuffixesSelector]);
        this.feed.fxSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedFxSuffixesSelector]);
        this.feed.afxSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedAfxSuffixesSelector]);
        this.feed.tseSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedTseSuffixesSelector]);
        this.feed.cfdSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedCfdSuffixesSelector]);
        this.feed.oc5SuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedOc5SuffixesSelector]);
        this.feed.cboSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedCboeOneSuffixesSelector]);
        this.feed.xetraSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedXetraSuffixesSelector]);
        this.feed.enxSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedEnxSuffixesSelector]);
        this.feed.xetraCoreEtfAndEtpLevel1SuffixSet = Helper.convertSpacesDataToArray(
            this.formGroup.value[AddNewFeedXetraCoreEtfsSuffixesSelector]
        );
        this.feed.hasImbalance = (this.feed.imbalanceSuffixSet && this.feed.imbalanceSuffixSet.length > 0);
        this.feed.suffixPriority = this.formGroup.value[AddNewFeedSuffixPrioritySelector];
    }

    private updateInfoPart() {
        this.feed.name = this.formGroup.value[AddNewFeedNameSelector];
        this.feed.description = this.formGroup.value[AddNewFeedDescriptionSelector];
        this.feed.exchange.id = this.formGroup.value[AddNewFeedMarketSelector];
        this.feed.platformFrontend.id = this.formGroup.value[AddNewFeedPlatformFrontendIdsSelector];
        this.feed.suffixPriority = this.formGroup.value[AddNewFeedSuffixPrioritySelector];
        this.feed.exclusionPortals = this.formGroup.value[AddNewFeedExclusionPortalIdsSelector];
    }

    private createMarketDataFeed(): ServerResponse<boolean> {
        const name = this.formGroup.value[AddNewFeedNameSelector];
        const description = this.formGroup.value[AddNewFeedDescriptionSelector];
        const exchangeId = Number(this.formGroup.value[AddNewFeedMarketSelector]);
        const hasl1 = this.hasL1;
        const hasl2 = this.hasL2;
        const platformFrontendId = this.formGroup.value[AddNewFeedPlatformFrontendIdsSelector];
        const exclusionPortalIds = this.formGroup.value[AddNewFeedExclusionPortalIdsSelector];
        const suffixPriority = this.formGroup.value[AddNewFeedSuffixPrioritySelector];

        const alaricProCost = this.getFormPriceValueByKey(AddNewFeedCostToAlaricProSelector);
        const alaricNonProCost = this.getFormPriceValueByKey(AddNewFeedCostToAlaricNonProSelector);
        const individualPricePro = this.getFormPriceValueByKey(AddNewFeedIndividualPriceProSelector);
        const individualPriceNonPro = this.getFormPriceValueByKey(AddNewFeedIndividualPriceNonProSelector);
        const alaricInternalPrice = this.getFormPriceValueByKey(AddNewFeedAlaricInternalPriceSelector);
        const currency = this.formGroup.value[AddNewFeedCurrencySelector] as string;

        const tradeSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedTradeSuffixesSelector]);
        const bboSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedBBOSuffixesSelector]);
        const excludeSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedExcludeSuffixesSelector]);
        const imbalanceSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedImbalanceSuffixesSelector]);
        const l2suffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedL2SuffixesSelector]);
        const nbboSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedNBBOSuffixesSelector]);
        const fxSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedFxSuffixesSelector]);
        const afxSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedAfxSuffixesSelector]);
        const tseSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedTseSuffixesSelector]);
        const cfdSuffixes = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedCfdSuffixesSelector]);
        const oc5SuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedOc5SuffixesSelector]);
        const cboSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedCboeOneSuffixesSelector]);
        const xetraSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedXetraSuffixesSelector]);
        const enxSuffixSet = Helper.convertSpacesDataToArray(this.formGroup.value[AddNewFeedEnxSuffixesSelector]);
        const xetraCoreEtfAndEtpLevel1SuffixSet = Helper.convertSpacesDataToArray(
            this.formGroup.value[AddNewFeedXetraCoreEtfsSuffixesSelector]
        );
        const reportMap = this.getReportMapByExchange(exchangeId);

        const inputModel = new MarketDataFeedInputModel(
            individualPricePro,
            individualPriceNonPro,
            alaricInternalPrice,
            alaricNonProCost,
            alaricProCost,
            bboSuffixes,
            excludeSuffixes,
            imbalanceSuffixes,
            nbboSuffixes,
            l2suffixes,
            tradeSuffixes,
            name,
            currency,
            description,
            exchangeId,
            hasl1,
            hasl2,
            platformFrontendId,
            suffixPriority,
            reportMap,
            fxSuffixes,
            afxSuffixes,
            cfdSuffixes,
            oc5SuffixSet,
            cboSuffixSet,
            exclusionPortalIds,
            tseSuffixes,
            xetraSuffixSet,
            enxSuffixSet,
            xetraCoreEtfAndEtpLevel1SuffixSet
        );

        return this.sesService.createMarketDataFeed(inputModel).pipe(
            switchMap((registerResponse: ServerResponseData<MarketDataFeedResponseInterface>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<MarketDataFeedResponseInterface>(
                        registerResponse,
                        MarketDataFeedsFormErrorStatesEnum.CreateMarketDataFeedFailed
                    );
                }

                return Helper.createSuccessResponseObservable<boolean>(true);
            })
        );
    }

    private getReportMapByExchange(exchangeId: number): Record<string, string> {
        const exchangeModel = this.exchanges.find(item => item.id === exchangeId);

        if (SesHelper.isNasdaqOrNasdaqUTPSelected(exchangeModel)) {
            const reportMap: Record<string, string> = {};
            reportMap[NasdaqReportMapsEnum.ProductCodeForPro] = this.formGroup.value[AddNewFeedProductCodeForProSelector];
            reportMap[NasdaqReportMapsEnum.ProductCodeForNonPro] = this.formGroup.value[AddNewFeedProductCodeForNonProSelector];
            reportMap[NasdaqReportMapsEnum.ReportTitleIdForPro] = this.formGroup.value[AddNewFeedReportTitleIdForProSelector];
            reportMap[NasdaqReportMapsEnum.ReportTitleIdForNonPro] = this.formGroup.value[AddNewFeedReportTitleIdForNonProSelector];

            return reportMap;
        }

        return {};
    }

    private initControl(control: BaseControl<any>): BaseControl {
        switch (control.name) {
            case AddNewFeedNameSelector:
                control.value = this.feed.name;
                break;
            case AddNewFeedDescriptionSelector:
                control.value = this.feed.description;
                break;
            case AddNewFeedSuffixPrioritySelector:
                control.value = this.feed.suffixPriority;
                break;
            case AddNewFeedMarketSelector:
                control.value = this.feed.exchange?.id;
                break;
            case AddNewFeedPlatformFrontendIdsSelector:
                control.value = this.feed.platformFrontend.id;
                break;
            case AddNewFeedExclusionPortalIdsSelector:
                control.value = this.feed.exclusionPortals;
                break;
            case AddNewFeedAlaricInternalPriceSelector:
                control.value = SesHelper.getPriceDataBy(this.feed.priceList, FeedPriceTypesEnum.AlaricInternal).price;
                break;
            case AddNewFeedCostToAlaricProSelector:
                control.value = SesHelper.getPriceDataBy(this.feed.priceList, FeedPriceTypesEnum.AlaricPro).price;
                break;
            case AddNewFeedCostToAlaricNonProSelector:
                control.value = SesHelper.getPriceDataBy(this.feed.priceList, FeedPriceTypesEnum.AlaricNonPro).price;
                break;
            case AddNewFeedIndividualPriceProSelector:
                control.value = SesHelper.getPriceDataBy(this.feed.priceList, FeedPriceTypesEnum.SelfPro).price;
                break;
            case AddNewFeedIndividualPriceNonProSelector:
                control.value = SesHelper.getPriceDataBy(this.feed.priceList, FeedPriceTypesEnum.SelfNonPro).price;
                break;
            case AddNewFeedHasL1Selector:
                control.value = this.feed.hasL1;
                break;
            case AddNewFeedHasL2Selector:
                control.value = this.feed.hasL2;
                break;
            case AddNewFeedHasImbalanceSelector:
                control.value = this.feed.hasImbalance;
                break;
            case AddNewFeedL2SuffixesSelector:
                control.value = this.feed.l2SuffixSet?.join(' ');
                break;
            case AddNewFeedBBOSuffixesSelector:
                control.value = this.feed.bboSuffixSet?.join(' ');
                break;
            case AddNewFeedNBBOSuffixesSelector:
                control.value = this.feed.nbboSuffixSet?.join(' ');
                break;
            case AddNewFeedCfdSuffixesSelector:
                control.value = this.feed.cfdSuffixSet?.join(' ');
                break;
            case AddNewFeedOc5SuffixesSelector:
                control.value = this.feed.oc5SuffixSet?.join(' ');
                break;
            case AddNewFeedCboeOneSuffixesSelector:
                control.value = this.feed.cboSuffixSet?.join(' ');
                break;
            case AddNewFeedFxSuffixesSelector:
                control.value = this.feed.fxSuffixSet?.join(' ');
                break;
            case AddNewFeedAfxSuffixesSelector:
                control.value = this.feed.afxSuffixSet?.join(' ');
                break;
            case AddNewFeedTseSuffixesSelector:
                control.value = this.feed.tseSuffixSet?.join(' ');
                break;
            case AddNewFeedXetraSuffixesSelector:
                control.value = this.feed.xetraSuffixSet?.join(' ');
                break;
            case AddNewFeedEnxSuffixesSelector:
                control.value = this.feed.enxSuffixSet?.join(' ');
                break;
            case AddNewFeedXetraCoreEtfsSuffixesSelector:
                control.value = this.feed.xetraCoreEtfAndEtpLevel1SuffixSet?.join(' ');
                break;
            case AddNewFeedExcludeSuffixesSelector:
                control.value = this.feed.excludedSuffixSet?.join(' ');
                break;
            case AddNewFeedProductCodeForProSelector:
                control.value = this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForPro];
                break;
            case AddNewFeedProductCodeForNonProSelector:
                control.value = this.feed.reportMap[NasdaqReportMapsEnum.ProductCodeForNonPro];
                break;
            case AddNewFeedReportTitleIdForProSelector:
                control.value = this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForPro];
                break;
            case AddNewFeedReportTitleIdForNonProSelector:
                control.value = this.feed.reportMap[NasdaqReportMapsEnum.ReportTitleIdForNonPro];
                break;
            case AddNewFeedTradeSuffixesSelector:
                control.value = this.feed.tradeSuffixSet?.join(' ');
                break;
            case AddNewFeedImbalanceSuffixesSelector:
                control.value = this.feed.imbalanceSuffixSet?.join(' ');
                break;
            case AddNewFeedCurrencySelector:
                control.value = SesHelper.getPriceCurrency(this.feed.priceList);
                break;
            default:
                control.value = '';
                break;
        }

        return control;
    }

    private prepareExchangeData(): void {
        if (!this.exchanges) {
            return;
        }

        this.exchangesAutoComplete = this.exchanges.map((item: ExchangeModel): SelectOptionInterface => {
            return {
                label: `${item.name} - ${item.code}`,
                value: item.id
            };
        });

        const exchanges: number[] = this.exchangesAutoComplete.map(d => {
            return Number(d.value);
        });

        const control = this.controls[2] as SelectControl;
        control.validators.push(InArrayValidator(exchanges));
        control.options = this.exchangesAutoComplete;
    }

    private prepareCurrencyData(): void {
        const currencies = [];
        const control = this.controls[17] as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode));
        control.extendValidators(InArrayValidator(currencies));
        control.disabled = control.options.length === 1;
    }

    private prepareSuffixPriortyData(): void {
        const priorities: number[] = [];
        this.suffixPrioritiesAutoComplete = [];

        for (let i = 0; i < this.SUFFIX_PRIORITY_LIMIT; i++) {
            this.suffixPrioritiesAutoComplete.push({
                label: i.toString(),
                value: i
            });
            priorities.push(Number(i));
        }

        const control = this.controls[15] as SelectControl;
        control.validators.push(InArrayValidator(priorities));
        control.options = this.suffixPrioritiesAutoComplete;
    }

    private preparePlatformFrontendData(): void {
        if (!this.platformFrontends) {
            return;
        }

        this.platformFrontendsAutoComplete = this.platformFrontends.map((item: PlatformFrontendInterface): SelectOptionInterface => {
            return {
                label: `${item.name}`,
                value: item.id
            };
        });

        const platformFrontends: number[] = this.platformFrontendsAutoComplete.map(d => {
            return Number(d.value);
        });

        const control = this.controls[3] as SelectControl;
        control.validators.push(InArrayValidator(platformFrontends));
        control.options = this.platformFrontendsAutoComplete;
    }

    private preparePortalsData(): void {
        if (!this.portals) {
            return;
        }

        this.portalsAutoComplete = this.portals.map((item: PortalInterface): SelectOptionInterface => {
            return {
                label: `${item.name}`,
                value: item.id
            };
        });

        const portals: number[] = this.portalsAutoComplete.map(d => {
            return Number(d.value);
        });

        const control = this.controls[28] as SelectControl;
        control.validators.push(InArrayValidator(portals));
        control.options = this.portalsAutoComplete;
    }

    updateFormControlsByUserSettings(feed: MarketDataFeedModel): void {
        this.feed = feed;
        this.initForm();
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        const feedControls = MarketDataFeedFormControls();

        if (this.formType === MarketDataFeedFormTypesEnum.Suffixes) {
            feedControls.push(new CheckboxControl({
                name: AddNewFeedHasL1Selector,
                value: this.feed.hasL1,
                placeholder: 'Has L1',
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: this.feed.hasL1,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: true
            }));

            feedControls.push(new CheckboxControl({
                name: AddNewFeedHasL2Selector,
                value: this.feed.hasL2,
                placeholder: 'Has L2',
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: this.feed.hasL2,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: true
            }));
        }

        this.controls = feedControls.map((control: BaseControl) => {
            if (this.feed) {
                control = this.initControl(control);
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.prepareSuffixPriortyData();
        this.prepareExchangeData();
        this.preparePlatformFrontendData();
        this.preparePortalsData();
        this.prepareCurrencyData();
    }

    createRequest(): ServerResponse<boolean | MarketDataFeedModel> {
        if (!this.formGroup.dirty) {
            return null;
        }

        if (!this.feed) {
            return this.createMarketDataFeed();
        }

        return this.updateMarketDataFeed();
    }
}
